import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { useLocalStorage } from '../hooks/useLocalStorage';
import { DEFAULT_COMPLEXITY_SCORE_THRESHOLD } from './dagComplexityAnalysis';

const LOCALSTORAGE_KEY = 'dbt-cloud.dag.complexity';

const zScores = z.object({
  djikstraAllScore: z.number(),
  floydWarshallScore: z.number(),
  topSortScore: z.number(),
});

const zStoredComplexity = z.object({
  scores: zScores,
  durationMillis: z.number().nullable(),
});

const zComplexity = z.object({
  highestSuccess: zStoredComplexity.nullable(),
  pendingComplexity: zStoredComplexity.nullable(),
  successThresholdMillis: z.number(),
});

const DEFAULT_SCORES = {
  djikstraAllScore: DEFAULT_COMPLEXITY_SCORE_THRESHOLD,
  floydWarshallScore: DEFAULT_COMPLEXITY_SCORE_THRESHOLD,
  topSortScore: DEFAULT_COMPLEXITY_SCORE_THRESHOLD,
};

type StoredComplexity = z.infer<typeof zStoredComplexity>;

type Scores = z.infer<typeof zScores>;

type Complexity = z.infer<typeof zComplexity>;

const shouldUpdateComplexity = (
  oldComplexity: StoredComplexity | null,
  newComplexity: StoredComplexity,
) => {
  if (oldComplexity === null) return true;
  if (oldComplexity.scores.djikstraAllScore < newComplexity.scores.djikstraAllScore)
    return true;
  if (oldComplexity.scores.floydWarshallScore < newComplexity.scores.floydWarshallScore)
    return true;
  if (oldComplexity.scores.topSortScore < newComplexity.scores.topSortScore)
    return true;
  return false;
};

/**
 * We keep track of the most complex thing we've rendered successfully,
 * where "success" is defined as faster than the {@link successThresholdMillis}.
 */
export const useSavedComplexity = (successThresholdMillis: number) => {
  const [savedComplexity, updateComplexity] = useLocalStorage<Complexity>(
    LOCALSTORAGE_KEY,
    (value: any) => {
      const parseResult = zComplexity.safeParse(value);
      return parseResult.success ? parseResult.data : null;
    },
    {
      highestSuccess: {
        scores: DEFAULT_SCORES,
        durationMillis: 0,
      },
      pendingComplexity: null,
      successThresholdMillis,
    },
  );

  const onLayoutComplete = useCallback(
    (durationMillis: number) => {
      updateComplexity(
        ({
          pendingComplexity,
          highestSuccess,
          successThresholdMillis: savedThreshold,
        }) => {
          const newComplexity: StoredComplexity = {
            scores: DEFAULT_SCORES,
            ...pendingComplexity,
            durationMillis,
          };
          if (
            (savedThreshold || -1) <= successThresholdMillis &&
            !shouldUpdateComplexity(highestSuccess, newComplexity)
          ) {
            return {
              pendingComplexity: null,
              highestSuccess,
              successThresholdMillis,
            };
          }

          /** Only update the highest success if it renders in less than {@link successThresholdMillis} */
          if (durationMillis < successThresholdMillis) {
            return {
              pendingComplexity: null,
              highestSuccess: {
                durationMillis,
                scores: {
                  djikstraAllScore: Math.max(
                    highestSuccess?.scores.djikstraAllScore ?? 0,
                    newComplexity.scores.djikstraAllScore,
                  ),
                  floydWarshallScore: Math.max(
                    highestSuccess?.scores.floydWarshallScore ?? 0,
                    newComplexity.scores.floydWarshallScore,
                  ),
                  topSortScore: Math.max(
                    highestSuccess?.scores.topSortScore ?? 0,
                    newComplexity.scores.topSortScore,
                  ),
                },
              },
              successThresholdMillis,
            };
          }
          return {
            pendingComplexity: null,
            highestSuccess,
            successThresholdMillis,
          };
        },
      );
    },
    [updateComplexity, successThresholdMillis],
  );

  return useMemo(
    () => ({
      onLayoutComplete,
      setPendingComplexity: (scores: Scores) =>
        updateComplexity((curr) => ({
          ...curr,
          pendingComplexity: {
            durationMillis: null,
            scores,
          },
        })),
      savedComplexity: savedComplexity.highestSuccess?.scores ?? DEFAULT_SCORES,
    }),
    [onLayoutComplete, savedComplexity.highestSuccess?.scores, updateComplexity],
  );
};
